import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ImageIcon, SendIcon, CheckIcon, XIcon, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from 'services/firebaseService';
import { IngredientList } from './IngredientList';
import { MacroSummary } from './MacroSummary';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { useUser } from 'contexts/UserContext';
import { Ingredient, Macros } from '@llm-macro-tracker/shared';
import { IngredientV2 } from '@llm-macro-tracker/shared';
import { useLocalStorage } from '@/hooks/use-local-storage';

interface QuickMealInputProps {
  onSubmit: (meal: any) => Promise<void>;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const defaultMacros: Macros = {
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
};

const containerVariants: Variants = {
  open: {
    height: 'auto',
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  closed: {
    height: 'auto',
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
};

const contentVariants: Variants = {
  open: {
    opacity: 1,
    height: 'auto',
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
    },
  },
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      opacity: { duration: 0.2 },
    },
  },
};

export function QuickMealInput({ onSubmit, isOpen, setIsOpen }: QuickMealInputProps) {
  const { uploadImage } = useUser();
  const [mealInput, setMealInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzedMeal, setAnalyzedMeal] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasModified, setHasModified] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [imageId, setImageId] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [mealSubmissionCount, setMealSubmissionCount] = useLocalStorage('mealSubmissionCount', 0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();

  useEffect(() => {
    const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
    setIsRTL(rtlRegex.test(mealInput));
  }, [mealInput]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [mealInput]);

  const calculateTotalMacros = (ingredients: Ingredient[]): Macros => {
    return ingredients.reduce(
      (total, ingredient) => ({
        calories: Math.round((total.calories + (ingredient.calories || 0)) * 10) / 10,
        protein: Math.round((total.protein + (ingredient.protein || 0)) * 10) / 10,
        carbs: Math.round((total.carbs + (ingredient.carbs || 0)) * 10) / 10,
        fat: Math.round((total.fat + (ingredient.fat || 0)) * 10) / 10,
      }),
      { ...defaultMacros },
    );
  };

  const handleSubmit = async () => {
    if (mealInput.trim()) {
      setIsAnalyzing(true);
      setError(null);
      try {
        const analyzeMeal = httpsCallable(functions, 'analyzeMeal');
        const sanitizedMealInput = DOMPurify.sanitize(mealInput);

        const result = await analyzeMeal({
          mealDescription: sanitizedMealInput,
          imageId: imageId ? imageId.split('/').pop() : undefined,
        });

        const analyzedMealData = result.data as any;
        if (analyzedMealData && analyzedMealData.success) {
          const ingredients = analyzedMealData.items.map((item: Ingredient) => ({
            ...item,
          }));
          const totalMacros = calculateTotalMacros(ingredients);
          setAnalyzedMeal({
            ...analyzedMealData,
            items: ingredients,
            totalMacros,
            mealDescription: mealInput,
            imageId,
          });
          setHasModified(false);
          setIsOpen(true);
        } else {
          throw new Error(analyzedMealData.error || 'Meal analysis failed');
        }
      } catch (error) {
        setError(
          error instanceof Error ? error.message : 'Failed to analyze meal. Please try again.',
        );
      } finally {
        setIsAnalyzing(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMealInput(e.target.value);
    setHasModified(true);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsUploading(true);
      setHasModified(true);

      try {
        const uploadedImageId = await uploadImage(file);
        setImageId(uploadedImageId);
        setIsImageUploaded(true);
      } catch (error) {
        console.error('Error uploading image:', error);
        setError('Failed to upload image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    }
  };

  const removeUploadedImage = () => {
    setImageId(undefined);
    setIsImageUploaded(false);
    setHasModified(true);
  };

  const resetForm = () => {
    setMealInput('');
    setAnalyzedMeal(null);
    setError(null);
    setHasModified(false);
    setIsImageUploaded(false);
    setImageId(undefined);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleSaveMeal = async () => {
    if (!analyzedMeal) return;

    setIsSaving(true);
    try {
      await onSubmit(analyzedMeal);

      const newCount = mealSubmissionCount + 1;
      setMealSubmissionCount(mealSubmissionCount + 1);

      // Show Tally feedback form after the third meal submission
      if (newCount === 3) {
        // @ts-ignore - Tally types are not available
        window.Tally?.openPopup('mKP1Jk', {
          width: 400,
          autoClose: 3000,
          hiddenFields: {
            email: user?.email || 'unknown',
            userId: user?.uid || 'unknown',
            source: 'quick_meal_input',
          },
        });
      }
      setIsClosing(true);
    } catch (error) {
      console.error('Error saving meal:', error);
      setError('Failed to save meal. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsClosing(true);
  };

  const handleAnimationComplete = (definition: string) => {
    if (definition === 'closed') {
      setIsClosing(false);
      setIsOpen(false);
      resetForm();
    }
  };

  const handleUpdateIngredient = (index: number, updatedIngredient: IngredientV2) => {
    if (!analyzedMeal) return;

    const newIngredients = [...analyzedMeal.items];
    newIngredients[index] = updatedIngredient;

    const newTotalMacros = calculateTotalMacros(newIngredients);

    setAnalyzedMeal({
      ...analyzedMeal,
      items: newIngredients,
      totalMacros: newTotalMacros,
    });
  };

  const handleDeleteIngredient = (index: number) => {
    if (!analyzedMeal) return;

    const newIngredients = analyzedMeal.items.filter((_: Ingredient, i: number) => i !== index);
    const newTotalMacros = calculateTotalMacros(newIngredients);

    setAnalyzedMeal({
      ...analyzedMeal,
      items: newIngredients,
      totalMacros: newTotalMacros,
    });
  };

  return (
    <div className="fixed left-0 right-0 bottom-0 bg-white overflow-hidden z-50 shadow-lg">
      <motion.div
        initial="closed"
        animate={isOpen && !isClosing ? 'open' : 'closed'}
        variants={containerVariants}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onAnimationComplete={handleAnimationComplete}
      >
        {/* Input Area - Always visible */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-stretch space-x-2">
            <div className="flex-grow relative">
              <textarea
                ref={textareaRef}
                placeholder="Describe your meal..."
                value={mealInput}
                onChange={handleInputChange}
                className={`w-full p-2 pr-10 border border-gray-300 rounded-md resize-none overflow-hidden ${
                  isRTL ? 'text-right' : 'text-left'
                }`}
                dir={isRTL ? 'rtl' : 'ltr'}
                rows={1}
                style={{ minHeight: '2.5rem', maxHeight: '10rem' }}
              />
              <Button
                size="icon"
                variant={isImageUploaded ? 'secondary' : 'ghost'}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                onClick={() =>
                  isImageUploaded
                    ? removeUploadedImage()
                    : document.getElementById('quickImageUpload')?.click()
                }
                disabled={isUploading}
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isImageUploaded ? (
                  <XIcon className="h-4 w-4" />
                ) : (
                  <ImageIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
            <input
              id="quickImageUpload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            {analyzedMeal && !hasModified ? (
              <>
                <Button
                  onClick={handleSaveMeal}
                  className="bg-green-500 hover:bg-green-600 text-white h-10 w-10 p-0"
                  disabled={isSaving || isClosing}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckIcon className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="h-10 w-10 p-0"
                  disabled={isSaving || isClosing}
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isAnalyzing || isUploading}
                className="h-10 w-10 p-0"
              >
                {isAnalyzing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <SendIcon className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          {error && <div className="text-red-500 mt-2">{error}</div>}
        </div>

        {/* Animated Content Area */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={contentRef}
              className="bg-white overflow-hidden border-t border-gray-200 shadow-inner"
              variants={contentVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <ScrollArea className="h-[calc(80vh-8rem)]">
                <div className="p-4">
                  <Card className="mb-4 shadow-sm">
                    <CardHeader className="bg-gray-50 border-b border-gray-100">
                      <CardTitle className="text-lg">
                        {analyzedMeal?.mealTitle || 'Analyzed Meal'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p
                        className={`text-sm text-gray-600 mb-4 ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        {analyzedMeal?.mealDescription}
                      </p>
                      <MacroSummary
                        calories={Math.round(analyzedMeal?.totalMacros.calories || 0)}
                        protein={Number((analyzedMeal?.totalMacros.protein || 0).toFixed(1))}
                        carbs={Number((analyzedMeal?.totalMacros.carbs || 0).toFixed(1))}
                        fat={Number((analyzedMeal?.totalMacros.fat || 0).toFixed(1))}
                        showPercentages
                      />
                    </CardContent>
                  </Card>
                  <h4 className="font-semibold text-md mb-2">Ingredients:</h4>
                  <IngredientList
                    ingredients={analyzedMeal?.items || []}
                    totalCalories={analyzedMeal?.totalMacros.calories || 0}
                    onUpdateIngredient={handleUpdateIngredient}
                    onDeleteIngredient={handleDeleteIngredient}
                  />
                </div>
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
